import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Checkbox, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import Toastify from "../../src/container/helper/Toastify";
import api from "../../src/container/helper/Api";
import { registerStudent } from "../../src/container/redux/registerStudentSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import LoginPopup from "./LoginPopup.js"; 
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import OtpVerificationModal from "./OtpVerificationModal.js";

const RegistrationPopup = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [checked, setChecked] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false); 

  // Validation schema
  const schema = yup.object().shape({
    email: yup
      .string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please enter valid email"
      )
      .required("Email is required"),
    password: yup
      .string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    name: yup.string().required("Full name is required"),
    phone: yup
      .string()
      .matches(/^\d{10}$/, "Phone must be 10 digits")
      .required("Phone number is required"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const {
    errors,
    values,
    handleChange,
    touched,
    handleBlur,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      email: "",
      password: "",
      name: "",
      phone: "",
      confirmPassword: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!checked) {
        Toastify.error("Please accept terms and conditions");
        return;
      }

      if (!captchaValue) {
        Toastify.error("Please verify the reCAPTCHA");
        return;
      }

      try {
        const { status } = await api.verifyCaptchaToken({
          token: captchaValue,
        });
        if (status === 200) {
          handleRegistration(values);
        } else {
          Toastify.error("Invalid captcha verification");
        }
      } catch (err) {
        Toastify.error("Captcha verification failed");
      }
    },
  });

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleRegistration = async (values) => {
    setLoading(true);
    try {
      // Store registration data for OTP verification
      setRegistrationData(values);
      
      // First step: Register student (this will send OTP)
      const result = await dispatch(registerStudent(values));
      unwrapResult(result);
      
      // Close registration popup and open OTP modal
      setShowOtpModal(true);
      
    } catch (error) {
      console.error("Registration failed:", error);
      Toastify.error(error?.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSuccess = () => {
    setShowOtpModal(false);
    onClose(); // Close registration popup
    resetForm();
    setChecked(false);
    setCaptchaValue(null);
    if (window.grecaptcha) {
      window.grecaptcha.reset();
    }
    if (onSuccess) {
      onSuccess(); // Call success callback if provided
    }
    Toastify.success("Registration successful! Please login.");
  };

  const handleOtpClose = () => {
    setShowOtpModal(false);
    // Optionally keep registration popup open
  };

  const handleLoginClick = () => {
    onClose(); // Close registration popup
    setShowLoginPopup(true); // Open login popup instead of navigating
  };


  const handleLoginPopupClose = () => {
    setShowLoginPopup(false);
  };

  if (!isOpen) return null;

  // Inline styles for the popup
  const popupStyles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    content: {
      backgroundColor: "white",
      padding: "30px",
      borderRadius: "10px",
      width: "90%",
      maxWidth: "500px",
      position: "relative",
      maxHeight: "90vh",
      overflowY: "auto",
    },
    closeButton: {
      position: "absolute",
      top: "10px",
      right: "10px",
      background: "none",
      border: "none",
      fontSize: "24px",
      cursor: "pointer",
      color: "#666",
    },
    title: {
      fontSize: "24px",
      fontWeight: "600",
      marginBottom: "20px",
      color: "#333",
    },
    label: {
      display: "block",
      marginBottom: "5px",
      fontWeight: "500",
      color: "#555",
    },
    inputBox: {
      display: "flex",
      alignItems: "center",
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      padding: "12px 15px",
      marginBottom: "5px",
      transition: "border-color 0.2s",
      backgroundColor: "#fafafa",
    },
    input: {
      flex: 1,
      border: "none",
      outline: "none",
      backgroundColor: "transparent",
      fontSize: "14px",
    },
    passwordToggle: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      color: "#666",
    },
    error: {
      color: "#f44336",
      fontSize: "12px",
      marginBottom: "10px",
      marginTop: "0",
    },
    captcha: {
      margin: "15px 0",
      transform: "scale(0.9)",
      transformOrigin: "0 0",
    },
    checkboxContainer: {
      display: "flex",
      alignItems: "center",
      margin: "15px 0",
    },
    checkboxLabel: {
      fontSize: "14px",
      color: "#666",
      marginLeft: "8px",
    },
    termsLink: {
      color: "#1D83F8",
      cursor: "pointer",
      textDecoration: "underline",
    },
    submitBtn: {
      backgroundColor: "#1D83F8",
      color: "white",
      border: "none",
      padding: "14px 20px",
      borderRadius: "8px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      width: "100%",
      fontSize: "16px",
      fontWeight: "500",
      transition: "background-color 0.2s",
    },
    submitBtnDisabled: {
      opacity: 0.7,
      cursor: "not-allowed",
    },
    loginLink: {
      textAlign: "center",
      marginTop: "20px",
      fontSize: "14px",
      color: "#666",
    },
    loginSpan: {
      color: "#1D83F8",
      cursor: "pointer",
      fontWeight: "500",
      marginLeft: "5px",
    },
    divider: {
      display: "flex",
      alignItems: "center",
      margin: "20px 0",
    },
    line: {
      flex: 1,
      height: "1px",
      backgroundColor: "#e0e0e0",
    },
    dividerText: {
      margin: "0 15px",
      color: "#999",
      fontSize: "14px",
    },
  };

  // Function to mask email for OTP modal
  const maskEmail = (email) => {
    if (!email || typeof email !== "string") {
      return "";
    }
    const [username, domain] = email.split("@");
    if (username.length <= 2) {
      return `${username[0]}***@${domain}`;
    }
    return `${username.substring(0, 2)}***@${domain}`;
  };

  return (
    <>
      <div style={popupStyles.overlay}>
        <div style={popupStyles.content}>
          <button style={popupStyles.closeButton} onClick={onClose}>
            ×
          </button>

          <h3 style={popupStyles.title}>Create your account</h3>

          <form onSubmit={handleSubmit}>
            {/* Full Name */}
            <div>
              <label style={popupStyles.label}>Full Name</label>
              <div style={popupStyles.inputBox}>
                <input
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your full name"
                  style={popupStyles.input}
                />
              </div>
              {errors.name && touched.name && (
                <p style={popupStyles.error}>{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label style={popupStyles.label}>Email</label>
              <div style={popupStyles.inputBox}>
                <input
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your email"
                  style={popupStyles.input}
                />
              </div>
              {errors.email && touched.email && (
                <p style={popupStyles.error}>{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label style={popupStyles.label}>Phone Number</label>
              <div style={popupStyles.inputBox}>
                <input
                  type="tel"
                  name="phone"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter 10-digit phone number"
                  style={popupStyles.input}
                  maxLength="10"
                />
              </div>
              {errors.phone && touched.phone && (
                <p style={popupStyles.error}>{errors.phone}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label style={popupStyles.label}>Password</label>
              <div style={popupStyles.inputBox}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Create a password"
                  style={popupStyles.input}
                />
                <div
                  onClick={() => setShowPassword(!showPassword)}
                  style={popupStyles.passwordToggle}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </div>
              </div>
              {errors.password && touched.password && (
                <p style={popupStyles.error}>{errors.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label style={popupStyles.label}>Confirm Password</label>
              <div style={popupStyles.inputBox}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Confirm your password"
                  style={popupStyles.input}
                />
                <div
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={popupStyles.passwordToggle}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </div>
              </div>
              {errors.confirmPassword && touched.confirmPassword && (
                <p style={popupStyles.error}>{errors.confirmPassword}</p>
              )}
            </div>

            {/* reCAPTCHA */}
            <div style={popupStyles.captcha}>
              <ReCAPTCHA
                sitekey="6Lc8vBcrAAAAANcniDVhDJlYjI_dim7gFk3L4u19"
                onChange={handleCaptchaChange}
              />
            </div>

            {/* Terms and Conditions */}
            <div style={popupStyles.checkboxContainer}>
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                size="small"
              />
              <span style={popupStyles.checkboxLabel}>
                I Agree with all of your{" "}
                <span style={popupStyles.termsLink}>Terms & Conditions</span>
              </span>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              style={{
                ...popupStyles.submitBtn,
                ...(loading ? popupStyles.submitBtnDisabled : {}),
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} thickness={4.5} color="inherit" />
              ) : (
                <>
                  Create Account <ArrowRightIcon className="w-5 h-5" />
                </>
              )}
            </button>

            {/* Divider */}
            <div style={popupStyles.divider}>
              <div style={popupStyles.line}></div>
              <span style={popupStyles.dividerText}>OR</span>
              <div style={popupStyles.line}></div>
            </div>

            {/* Login Link */}
            <div style={popupStyles.loginLink}>
              Already have an account?
              <span style={popupStyles.loginSpan} onClick={handleLoginClick}>
                Login here
              </span>
            </div>
          </form>
        </div>
      </div>

      {/* OTP Verification Modal */}
      <OtpVerificationModal
        isOpen={showOtpModal}
        onClose={handleOtpClose}
        onSuccess={handleOtpSuccess}
        email={registrationData?.email}
        registrationData={registrationData}
        desc={
          registrationData?.email
            ? `We have sent you an OTP to ${maskEmail(
                registrationData.email
              )}, please check and verify.`
            : "Please enter the OTP sent to your email"
        }
      />
    </>
  );
};

export default RegistrationPopup;